/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React, { type ReactNode, type ComponentType, type SVGProps } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { FIELD_TYPES } from '@jira/polaris__domain-field/src/isSystemFieldType.tsx';
import type { FieldType } from '@jira/polaris__domain-field/src/FieldType.types.tsx';
import {
	VIEW_KIND_BOARD,
	VIEW_KIND_MATRIX,
	VIEW_KIND_TABLE,
	VIEW_KIND_TIMELINE,
	VIEW_KIND_TREE,
} from '@jira/polaris__domain-view/src/viewKindTimeline.constants.tsx';
import type { ViewKind } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import type { FieldConfiguration } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import {
	autosaveOffGlyph,
	autosaveOnGlyph,
} from '@jira/polaris__lib-glyphs/src/autosaveOnGlyph.tsx';
import { boardGlyph } from '@jira/polaris__lib-glyphs/src/boardGlyph.tsx';
import { bulletListGlyph } from '@jira/polaris__lib-glyphs/src/bulletListGlyph.tsx';
import { cardLayoutGlyph } from '@jira/polaris__lib-glyphs/src/cardLayoutGlyph.tsx';
import { checkboxGlyph } from '@jira/polaris__lib-glyphs/src/checkboxGlyph.tsx';
import { chevronRightGlyph } from '@jira/polaris__lib-glyphs/src/chevronRightGlyph.tsx';
import { chevronUpGlyph } from '@jira/polaris__lib-glyphs/src/chevronUpGlyph.tsx';
import { commentGlyph } from '@jira/polaris__lib-glyphs/src/commentGlyph.tsx';
import { datapointGlyph } from '@jira/polaris__lib-glyphs/src/datapointGlyph.tsx';
import { dateGlyph } from '@jira/polaris__lib-glyphs/src/dateGlyph.tsx';
import { deliveryTicketsGlyph } from '@jira/polaris__lib-glyphs/src/deliveryTicketsGlyph.tsx';
import { dropdownGlyph } from '@jira/polaris__lib-glyphs/src/dropdownGlyph.tsx';
import {
	atlasGoalGlyph,
	atlasProjectGlyph,
	atlasProjectStatusGlyph,
} from '@jira/polaris__lib-glyphs/src/atlasProjectStatusGlyph.tsx';
import { formulaGlyph } from '@jira/polaris__lib-glyphs/src/formulaGlyph.tsx';
import { hiddenGlyph } from '@jira/polaris__lib-glyphs/src/hiddenGlyph.tsx';
import { insightsGlyph } from '@jira/polaris__lib-glyphs/src/insightsGlyph.tsx';
import { issueTypeGlyph } from '@jira/polaris__lib-glyphs/src/issueTypeGlyph.tsx';
import { keyGlyph } from '@jira/polaris__lib-glyphs/src/keyGlyph.tsx';
import { labelGlyph } from '@jira/polaris__lib-glyphs/src/labelGlyph.tsx';
import { likeIconFilledGlyph } from '@jira/polaris__lib-glyphs/src/likeIconFilledGlyph.tsx';
import { linkGlyph } from '@jira/polaris__lib-glyphs/src/linkGlyph.tsx';
import { matrixGlyph } from '@jira/polaris__lib-glyphs/src/matrixGlyph.tsx';
import { mergeIdeasGlyph } from '@jira/polaris__lib-glyphs/src/mergeIdeasGlyph.tsx';
import { numberGlyph } from '@jira/polaris__lib-glyphs/src/numberGlyph.tsx';
import { peopleGlyph } from '@jira/polaris__lib-glyphs/src/peopleGlyph.tsx';
import { pollGlyph } from '@jira/polaris__lib-glyphs/src/pollGlyph.tsx';
import { projectGlyph } from '@jira/polaris__lib-glyphs/src/projectGlyph.tsx';
import { ratingGlyph } from '@jira/polaris__lib-glyphs/src/ratingGlyph.tsx';
import { reactionGlyph } from '@jira/polaris__lib-glyphs/src/reactionGlyph.tsx';
import {
	setUpXAxisGlyph,
	setUpYAxisGlyph,
} from '@jira/polaris__lib-glyphs/src/setUpXAxisGlyph.tsx';
import { shortTextGlyph } from '@jira/polaris__lib-glyphs/src/shortTextGlyph.tsx';
import { sliderGlyph } from '@jira/polaris__lib-glyphs/src/sliderGlyph.tsx';
import {
	sortAscMatrixGlyph,
	sortDescMatrixGlyph,
} from '@jira/polaris__lib-glyphs/src/sortDescMatrixGlyph.tsx';
import { statusGlyph } from '@jira/polaris__lib-glyphs/src/statusGlyph.tsx';
import { switchGlyph } from '@jira/polaris__lib-glyphs/src/switchGlyph.tsx';
import { timelineGlyph } from '@jira/polaris__lib-glyphs/src/timelineGlyph.tsx';
import { timeStampGlyph } from '@jira/polaris__lib-glyphs/src/timeStampGlyph.tsx';
import { upvoteGlyph } from '@jira/polaris__lib-glyphs/src/upvoteGlyph.tsx';
import { visibleGlyph } from '@jira/polaris__lib-glyphs/src/visibleGlyph.tsx';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import DepartmentIcon from '@atlaskit/icon/core/department';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import AkScalesIcon from '@atlaskit/icon/core/scales';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import { token } from '@atlaskit/tokens';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import { JiraIcon } from '@atlaskit/logo';
import TeamsIcon from '@atlaskit/icon/core/teams';
import type { IconProps } from '@atlaskit/icon/types';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { PolarisIconProps } from './PolarisIconProps.types.tsx';
import { ConnectionFieldIcon } from './ConnectionFieldIcon.tsx';

const iconWrapperStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& svg': {
			width: token('space.200'),
			height: token('space.200'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& span': {
			width: token('space.200'),
			height: token('space.200'),
			display: 'flex',
			alignItems: 'center',
			position: 'static',
		},
	},
});

const sizeStyles = cssMap({
	small: {
		width: '16px',
		height: '16px',
	},
	medium: {
		width: '24px',
		height: '24px',
	},
	large: {
		width: '32px',
		height: '32px',
	},
	xlarge: {
		width: '48px',
		height: '48px',
	},
});

const iconStyles = css({
	display: 'inline-block',
	flexShrink: 0,
	'--icon-secondary-color': token('elevation.surface'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> svg': {
		width: '100%',
		height: '100%',
		maxWidth: '100%',
		maxHeight: '100%',
		color: 'var(--icon-primary-color)',
		fill: token('elevation.surface'),
		overflow: 'hidden',
		pointerEvents: 'none',
		verticalAlign: 'bottom',
	},
});

interface GlyphProps extends SVGProps<SVGSVGElement> {
	'data-testid'?: string;
	'aria-label'?: string;
	className?: string;
}

interface CustomIconProps {
	label: string;
	primaryColor?: string;
	secondaryColor?: string;
	size?: 'small' | 'medium' | 'large' | 'xlarge';
	glyph?: ComponentType<GlyphProps>;
}

// Custom icon ejection - these icons have been migrated away from the deprecated Custom / SVG components to native SVG. Please review whether this icon should be contributed to @atlaskit/icon-lab or whether it can be replaced by an existing icon from either @atlaskit/icon or @atlaskit/icon-lab
export const CustomIcon = ({
	label,
	size = 'medium',
	primaryColor = 'currentColor',
	secondaryColor,
	glyph: Glyph,
}: CustomIconProps): React.JSX.Element => {
	return (
		<span
			role={label ? 'img' : undefined}
			aria-label={label}
			aria-hidden={label ? undefined : true}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				color: primaryColor,
				fill: secondaryColor,
			}}
			css={[iconStyles, sizeStyles[size]]}
		>
			{Glyph && <Glyph role="presentation" />}
		</span>
	);
};

const FIELD_TYPE_GLYPH_MAPPING = {
	[FIELD_TYPES.ASSIGNEE]: [peopleGlyph, 'assignee'],
	[FIELD_TYPES.CREATOR]: [peopleGlyph, 'creator'],
	[FIELD_TYPES.REPORTER]: [peopleGlyph, 'reporter'],
	[FIELD_TYPES.SUMMARY]: [shortTextGlyph, 'summary'],
	[FIELD_TYPES.STATUS]: [statusGlyph, 'status'],
	[FIELD_TYPES.ISSUE_KEY]: [keyGlyph, 'issue key'],
	[FIELD_TYPES.ISSUE_TYPE]: [issueTypeGlyph, 'issue type'],
	[FIELD_TYPES.SHORT_TEXT]: [shortTextGlyph, 'short text'],
	[FIELD_TYPES.HYPERLINK]: [linkGlyph, 'hyperlink'],
	[FIELD_TYPES.DATE]: [dateGlyph, 'date'],
	[FIELD_TYPES.NUMBER]: [numberGlyph, 'number'],
	[FIELD_TYPES.UPDATED]: [timeStampGlyph, 'updated'],
	[FIELD_TYPES.CREATED]: [timeStampGlyph, 'created'],
	[FIELD_TYPES.LABELS]: [labelGlyph, 'labels'],
	[FIELD_TYPES.CUSTOM_LABELS]: [labelGlyph, 'labels'],
	[FIELD_TYPES.SINGLE_SELECT]: [dropdownGlyph, 'single select dropdown'],
	[FIELD_TYPES.MULTI_SELECT]: [dropdownGlyph, 'multi select dropdown'],
	[FIELD_TYPES.JSW_MULTI_SELECT]: [dropdownGlyph, 'multi select dropdown'],
	[FIELD_TYPES.PEOPLE]: [peopleGlyph, 'users'],
	[FIELD_TYPES.JSW_PEOPLE]: [peopleGlyph, 'users'],
	[FIELD_TYPES.REACTIONS]: [reactionGlyph, 'reactions'],
	[FIELD_TYPES.CHECKBOX]: [checkboxGlyph, 'checkbox'],
	[FIELD_TYPES.SLIDER]: [sliderGlyph, 'slider'],
	[FIELD_TYPES.RATING]: [ratingGlyph, 'rating'],
	[FIELD_TYPES.FORMULA]: [formulaGlyph, 'formula'],
	[FIELD_TYPES.VOTES]: [likeIconFilledGlyph, 'play'],
	[FIELD_TYPES.ISSUE_COMMENTS]: [commentGlyph, 'comments'],
	[FIELD_TYPES.LINKED_ISSUES]: [linkGlyph, 'linked issues'],
	[FIELD_TYPES.INSIGHTS]: [insightsGlyph, 'insights'],
	[FIELD_TYPES.INSIGHT_IMPACT]: [insightsGlyph, 'insight impact'],
	[FIELD_TYPES.ATLAS_GOAL]: [atlasGoalGlyph, 'Atlas goals'],
	[FIELD_TYPES.ATLAS_PROJECT]: [atlasProjectGlyph, 'Atlas project'],
	[FIELD_TYPES.ATLAS_PROJECT_STATUS]: [atlasProjectStatusGlyph, 'Atlas project status'],
	[FIELD_TYPES.PLATFORM_GOALS]: [atlasGoalGlyph, 'goals'],
	[FIELD_TYPES.PLATFORM_ATLAS_PROJECT]: [atlasProjectGlyph, 'project'],
	// Cleanup to be removed with project-terminology-refresh cleanup
	[FIELD_TYPES.PROJECT]: [projectGlyph, 'project'],
} as const;

const isFieldTypeGlyphMappingKey = (
	fieldType: FieldType,
): fieldType is keyof typeof FIELD_TYPE_GLYPH_MAPPING => fieldType in FIELD_TYPE_GLYPH_MAPPING;

export const iconForPolarisFieldType = (
	fieldType?: FieldType,
	iconProps?: IconProps | null,
	configuration?: FieldConfiguration,
): ReactNode | null => {
	// we need 'isWeightedField' because some field types map to multiple icons, eg. MULTI_SELECT_FIELD_TYPE
	const isWeightedField = configuration?.optionWeightType !== undefined;
	const iconPropsInner = iconProps ?? {};

	if (fieldType === undefined) {
		return null;
	}

	if (isWeightedField === true) {
		return <AkScalesIcon size="small" label="weight" {...iconPropsInner} />;
	}

	if (fieldType === FIELD_TYPES.DELIVERY_STATUS) {
		return (
			<IconWrapper>
				<JiraIcon size="small" {...iconPropsInner} label="delivery status" />
			</IconWrapper>
		);
	}

	if (fieldType === FIELD_TYPES.DELIVERY_PROGRESS) {
		return (
			<IconWrapper>
				<JiraIcon size="small" {...iconPropsInner} label="delivery progress" />
			</IconWrapper>
		);
	}

	if (
		fieldType === FIELD_TYPES.PROJECT &&
		expVal('project-terminology-refresh', 'isEnabled', false)
	) {
		return <SpacesIcon label="" />;
	}

	if (fieldType === FIELD_TYPES.INTERVAL) {
		return <CalendarIcon label="date interval" {...iconPropsInner} />;
	}

	if (fieldType === FIELD_TYPES.ISSUE_ID) {
		return <EyeOpenIcon label="ideas" {...iconPropsInner} />;
	}

	if (isFieldTypeGlyphMappingKey(fieldType)) {
		const [glyph, label] = FIELD_TYPE_GLYPH_MAPPING[fieldType];

		return <CustomIcon glyph={glyph} size="small" label={label} {...iconPropsInner} />;
	}

	if (fieldType === FIELD_TYPES.DESCRIPTION) {
		return <AlignTextLeftIcon label="overview" color={token('color.icon')} {...iconPropsInner} />;
	}

	if (fieldType === FIELD_TYPES.CONNECTION) {
		return <ConnectionFieldIcon issueTypeFilters={configuration?.issueTypeFilters} />;
	}

	if (fieldType === FIELD_TYPES.TEAM) {
		return <TeamsIcon label="team" {...iconPropsInner} />;
	}

	return null;
};

export const PolarisIconType = {
	Department: 'PolarisIconDepartment',
	ChevronRight: 'PolarisIconChevronRight',
	ChevronUp: 'PolarisIconChevronUp',
	Datapoint: 'PolarisIconDatapoint',
	Board: 'PolarisIconBoard',
	Matrix: 'PolarisIconMatrix',
	Timeline: 'PolarisIconTimeline',
	BulletList: 'PolarisIconBulletList',
	Visible: 'PolarisIconVisible',
	Hidden: 'PolarisIconHidden',
	Scales: 'PolarisIconScales',
	Downvote: 'PolarisIconDownvote',
	Upvote: 'PolarisIconUpvote',
	Poll: 'PolarisIconPoll',
	Switch: 'PolarisIconSwitch',
	AutosaveOff: 'PolarisIconAutosaveOff',
	AutosaveOn: 'PolarisIconAutosaveOn',
	SortMatrixAsc: 'PolarisIconSortMatrixAsc',
	SortMatrixDesc: 'PolarisIconSortMatrixDesc',
	DisplaySettings: 'PolarisIconDisplaySettings',
	MergeIdeas: 'PolarisIconMergeIdeas',
	DeliveryTickets: 'PolarisIconDeliveryTickets',
	SetUpXAxis: 'PolarisIconSetUpXAxis',
	SetUpYAxis: 'PolarisIconSetUpYAxis',
	Project: 'PolarisIconProject',
	CardLayout: 'PolarisIconCardLayout',
};

export const PolarisIcon = (props: PolarisIconProps): React.JSX.Element | null => {
	switch (props.name) {
		case PolarisIconType.ChevronRight:
			return <CustomIcon glyph={chevronRightGlyph} {...props} />;

		case PolarisIconType.ChevronUp:
			return <CustomIcon glyph={chevronUpGlyph} {...props} />;

		case PolarisIconType.Datapoint:
			return <CustomIcon glyph={datapointGlyph} {...props} />;

		case PolarisIconType.Board:
			return <CustomIcon glyph={boardGlyph} {...props} />;

		case PolarisIconType.Matrix:
			return <CustomIcon glyph={matrixGlyph} {...props} />;

		case PolarisIconType.Timeline:
			return <CustomIcon glyph={timelineGlyph} {...props} />;

		case PolarisIconType.Department:
			return <DepartmentIcon label={props.label} />;

		case PolarisIconType.BulletList:
			return <CustomIcon glyph={bulletListGlyph} {...props} />;

		case PolarisIconType.Visible:
			return <CustomIcon glyph={visibleGlyph} {...props} />;

		case PolarisIconType.Hidden:
			return <CustomIcon glyph={hiddenGlyph} {...props} />;

		case PolarisIconType.Scales:
			return (
				<AkScalesIcon label={props.label} size={props.size === 'small' ? 'small' : 'medium'} />
			);

		case PolarisIconType.Downvote:
			return <CustomIcon glyph={upvoteGlyph} {...props} />;

		case PolarisIconType.Upvote:
			return <CustomIcon glyph={upvoteGlyph} {...props} />;

		case PolarisIconType.Poll:
			return <CustomIcon glyph={pollGlyph} {...props} />;

		case PolarisIconType.Switch:
			return <CustomIcon glyph={switchGlyph} {...props} />;

		case PolarisIconType.AutosaveOff: {
			const AutosaveOffIcon = autosaveOffGlyph; // Not using CustomIcon because autosaveOffGlyph is 20x24
			return <AutosaveOffIcon />;
		}

		case PolarisIconType.AutosaveOn: {
			const AutosaveOnIcon = autosaveOnGlyph; // Not using CustomIcon because autosaveOnGlyph is 20x24

			return <AutosaveOnIcon />;
		}

		case PolarisIconType.SortMatrixAsc:
			return <CustomIcon glyph={sortAscMatrixGlyph} {...props} />;

		case PolarisIconType.SortMatrixDesc:
			return <CustomIcon glyph={sortDescMatrixGlyph} {...props} />;

		case PolarisIconType.MergeIdeas:
			return <CustomIcon glyph={mergeIdeasGlyph} {...props} />;

		case PolarisIconType.DeliveryTickets:
			return <CustomIcon glyph={deliveryTicketsGlyph} {...props} />;

		case PolarisIconType.SetUpXAxis:
			return <CustomIcon glyph={setUpXAxisGlyph} {...props} />;

		case PolarisIconType.SetUpYAxis:
			return <CustomIcon glyph={setUpYAxisGlyph} {...props} />;

		case PolarisIconType.Project:
			return expVal('project-terminology-refresh', 'isEnabled', false) ? null : (
				<CustomIcon glyph={projectGlyph} {...props} />
			);

		case PolarisIconType.CardLayout:
			return <CustomIcon glyph={cardLayoutGlyph} {...props} />;

		case PolarisIconType.DisplaySettings:
			return <CustomIcon glyph={sliderGlyph} {...props} />;

		default:
			return null;
	}
};

export const ViewItemIcon = ({
	viewKind,
	size,
}: {
	viewKind?: ViewKind | string;
	size?: IconProps['size'];
}): React.JSX.Element | null => {
	if (viewKind === VIEW_KIND_TABLE) {
		return <PolarisIcon name={PolarisIconType.BulletList} size={size ?? 'medium'} label="" />;
	}

	if (viewKind === VIEW_KIND_BOARD) {
		return <PolarisIcon name={PolarisIconType.Board} size={size ?? 'medium'} label="" />;
	}

	if (viewKind === VIEW_KIND_MATRIX) {
		return <PolarisIcon name={PolarisIconType.Matrix} size={size ?? 'medium'} label="" />;
	}

	if (viewKind === VIEW_KIND_TIMELINE) {
		return <PolarisIcon name={PolarisIconType.Timeline} size={size ?? 'medium'} label="" />;
	}

	if (viewKind === VIEW_KIND_TREE) {
		if (fg('jpd_tree-view')) {
			return <PolarisIcon name={PolarisIconType.Department} size={size ?? 'medium'} label="" />;
		}

		return <PolarisIcon name={PolarisIconType.BulletList} size={size ?? 'medium'} label="" />;
	}

	return null;
};

const IconWrapper = ({ children }: React.PropsWithChildren<{}>) => (
	<div css={iconWrapperStyles.root}>{children}</div>
);

export const getIconWithProps =
	(providedProps: Partial<IconProps>): ComponentType<IconProps> =>
	(props: IconProps) => <CustomIcon {...props} {...providedProps} />;
